/**
 * RareScaleEducational.jsx — Educational version of the 4-act rare disease magnitude page
 *
 * Act 1: Dot particle field — 300M people
 * Act 2: Disease bubble chart — 7,000 conditions
 * Act 3: Gene therapy overlap network
 * Act 4: Approval timeline — accelerating momentum
 *
 * Install: npm install gsap
 */

import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ContactFooter from '../components/ContactFooter';

gsap.registerPlugin(ScrollTrigger);

// ─── FONTS ────────────────────────────────────────────────────────────────────
const FONT_URL =
  'https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=IBM+Plex+Mono:wght@300;400;500&family=Syne:wght@400;500;700&display=swap';

// ─── ACT 1 COPY ───────────────────────────────────────────────────────────────
const ACT1_LINES = [
  { at: 0,    text: 'This dot represents one person.' },
  { at: 0.08, text: "On average, patients with a rare disease see multiple specialists\nover several years before receiving a diagnosis.\nMost conditions affect fewer than a few hundred people worldwide." },
  { at: 0.22, text: 'Each individual case is rare.\nBut taken together, rare diseases affect a large population.' },
  { at: 0.34, text: 'In the United States, a rare disease or condition affects fewer than 200,000 people.\nIn the EU, a rare disease affects fewer than 5 people in 10,000.' },
  { at: 0.48, text: 'There are over 7,000 recognized rare diseases.' },
  { at: 0.58, text: <>Each dot represents a person living with a rare disease.{'\n'}Approximately 70% of rare diseases have a childhood onset.{'\n'}<span style={{ color: '#dde3f0', fontWeight: 700 }}>Approximately 30% of children with a rare disease will not survive past age five.</span></> },
  { at: 0.78, text: '300 million people worldwide\nlive with a rare disease.\n\nThat exceeds the entire population of the United States.\nCollectively, rare diseases are not so rare.' },
];

// ─── ACT 2: DISEASES ──────────────────────────────────────────────────────────
// ─── DISEASES — global prevalence estimates ────────────────────────────────
// Sources: GBD 2021, WFH Annual Survey 2022, Guo et al. Pediatr Pulmonol 2024,
// Medina et al. Mov Disord 2022, Salari et al. J Orthop Surg Res 2022,
// GBD 2021 SCD Collaborators (Lancet Haematol 2023), TIF registry,
// FARA (Lancet Neurol 2025), Rare Disease Advisor 2023–2024.
// All figures are global living-patient estimates (diagnosed/treated where noted).

const DISEASES = [
  { name: 'Cystic Fibrosis',         patients: 105000,   color: '#00e5b0', category: 'Pulmonary'    }, // diagnosed globally (Guo 2024); ~188k estimated incl. undiagnosed
  { name: "Huntington's",            patients: 390000,   color: '#ff6b8a', category: 'Neurological' }, // 4.88/100k × 8B pop (Medina 2022)
  { name: 'Duchenne MD',             patients: 300000,   color: '#b48cfa', category: 'Muscular'     }, // ~4.8/100k general pop (Salari 2022)
  { name: 'Spinal Muscular Atrophy', patients: 70000,    color: '#00e5b0', category: 'Neurological' }, // living patients; post-treatment survival improving
  { name: 'PKU',                     patients: 75000,    color: '#f5a623', category: 'Metabolic'    }, // diagnosed/treated in HIC; global true burden far higher
  { name: 'Gaucher Disease',         patients: 60000,    color: '#f5a623', category: 'Metabolic'    }, // 0.9/100k global (meta-analysis, PMC 2023)
  { name: 'Fabry Disease',           patients: 50000,    color: '#f5a623', category: 'Metabolic'    }, // diagnosed globally; true burden 3× higher (late-onset undiagnosed)
  { name: 'Hemophilia A',            patients: 210000,   color: '#ff6b8a', category: 'Hematologic'  }, // WFH Annual Survey 2022 (diagnosed); true ~900k
  { name: 'Hemophilia B',            patients: 42000,    color: '#ff6b8a', category: 'Hematologic'  }, // WFH Annual Survey 2022 (diagnosed)
  { name: 'Sickle Cell',             patients: 7700000,  color: '#ff6b8a', category: 'Hematologic'  }, // GBD 2021: 7.74M globally (100k is US-only)
  { name: 'Beta-Thalassemia',        patients: 200000,   color: '#ff6b8a', category: 'Hematologic'  }, // ~200k on chronic transfusion globally (TIF); 60k = annual births, not prevalence
  { name: 'Rett Syndrome',           patients: 300000,   color: '#b48cfa', category: 'Neurological' }, // ~1:10k–15k girls; ~4B females globally
  { name: 'Pompe Disease',           patients: 50000,    color: '#f5a623', category: 'Metabolic'    }, // global living estimate; birth prev 2.0/100k (meta-analysis 2024)
  { name: 'ALS',                     patients: 250000,   color: '#b48cfa', category: 'Neurological' }, // ~222k in 2015 (Nature Comms); ~250k now (30k is US-only)
  { name: 'Marfan Syndrome',         patients: 1600000,  color: '#5bc8f5', category: 'Connective'   }, // 1:5,000 globally (Marfan Foundation); 200k is US-only
  { name: 'Osteogenesis Imperfecta', short: 'OI', patients: 400000, color: '#5bc8f5', category: 'Connective'   }, // 1:20,000 globally
  { name: "Wilson's Disease",        patients: 200000,   color: '#f5a623', category: 'Metabolic'    }, // 1:30,000 globally
  { name: 'Prader-Willi',            patients: 400000,   color: '#b48cfa', category: 'Neurological' }, // 350k–400k worldwide (Rare Disease Advisor 2023)
  { name: 'Angelman Syndrome',       patients: 500000,   color: '#b48cfa', category: 'Neurological' }, // 1:15,000–20,000 globally
  { name: 'MCAD Deficiency',         patients: 150000,   color: '#f5a623', category: 'Metabolic'    }, // diagnosed in HIC via NBS; global true burden higher
  { name: "Friedreich's Ataxia",     patients: 40000,    color: '#b48cfa', category: 'Neurological' }, // ~0.5/100k global (Lancet Neurol 2025); FARA quotes 15k (undercount)
  { name: 'MPS I (Hurler)',          patients: 25000,    color: '#f5a623', category: 'Metabolic'    }, // ~1:100,000 globally; many undiagnosed
  { name: 'Limb-Girdle MD',  short: 'LGMD', patients: 200000, color: '#b48cfa', category: 'Muscular'     }, // 0.7–7.3/100k globally; 7k is US-only
  { name: 'Pyridoxine-Dependent Epilepsy', patients: 500, color: '#f5a623', category: 'Metabolic'  }, // ~300–500 molecularly confirmed cases worldwide; 1,200 was overstated even for global
];

const CATEGORY_COLORS = {
  Neurological: '#b48cfa',
  Metabolic:    '#f5a623',
  Hematologic:  '#ff6b8a',
  Pulmonary:    '#00e5b0',
  Muscular:     '#5bc8f5',
  Connective:   '#5bc8f5',
};

// ─── ACT 3: THERAPY PLATFORMS ─────────────────────────────────────────────────
const PLATFORMS = [
  {
    id: 'aav',
    name: 'AAV\nGene Therapy',
    color: '#00e5b0',
    x: 0.5, y: 0.22,
    proof: 'Zolgensma (SMA, 2019), Hemgenix (Hemophilia B, 2022), Roctavian (Hemophilia A, 2023), Elevidys (DMD, 2023)',
    desc: 'Adeno-associated viral vectors deliver a corrected gene copy directly into cells. Now has multiple approved systemic therapies: Zolgensma for SMA, Hemgenix for hemophilia B, Roctavian for hemophilia A, and Elevidys for DMD. Programs for Friedreich\'s ataxia and PDE are in early-stage clinical trials.',
    diseases: ['Spinal Muscular Atrophy', 'Duchenne MD', 'Hemophilia A', 'Hemophilia B', 'Pompe Disease', "Friedreich's Ataxia", 'Pyridoxine-Dependent Epilepsy'],
  },
  {
    id: 'aso',
    name: 'Antisense\nOligonucleotides',
    color: '#b48cfa',
    x: 0.18, y: 0.55,
    proof: 'Spinraza (SMA, 2016) — first intrathecal CNS ASO; Qalsody (SOD1-ALS, 2023)',
    desc: 'Short synthetic nucleic acid strands that intercept and correct faulty RNA before it produces a dysfunctional protein. Spinraza was the first intrathecal CNS ASO and first disease-modifying therapy for SMA. Four exon-skipping ASOs are now approved for Duchenne MD (eteplirsen, golodirsen, viltolarsen, casimersen). Tofersen (Qalsody) was approved in 2023 for SOD1-ALS.',
    diseases: ['Spinal Muscular Atrophy', "Huntington's", 'ALS', 'Duchenne MD'],
  },
  {
    id: 'crispr',
    name: 'CRISPR\nEditing',
    color: '#f5a623',
    x: 0.82, y: 0.55,
    proof: 'Casgevy (Sickle Cell, Dec 2023; Beta-Thalassemia, Jan 2024) — first CRISPR therapy approved',
    desc: 'Precise gene-editing tools that can correct DNA at a specific location. Casgevy was approved for sickle cell disease (Dec 8, 2023) and then for transfusion-dependent beta-thalassemia (Jan 16, 2024) — two separate approvals six weeks apart. CRISPR approaches for Duchenne MD and Huntington\'s are in preclinical and early-stage investigation.',
    diseases: ['Sickle Cell', 'Beta-Thalassemia', 'Duchenne MD', "Huntington's"],
  },
  {
    id: 'ert',
    name: 'Enzyme\nReplacement',
    color: '#ff6b8a',
    x: 0.18, y: 0.82,
    proof: 'Ceredase (Gaucher, 1991) — established proof of concept',
    desc: 'Replaces the missing or deficient enzyme directly. First proven in Gaucher disease in 1991, the approach has since been applied to Fabry (Fabrazyme, US 2003), Pompe (Myozyme, 2006), and several MPS disorders including MPS I (Aldurazyme, 2003), MPS II, MPS VI, and MPS IVA.',
    diseases: ['Gaucher Disease', 'Fabry Disease', 'Pompe Disease', 'MPS I (Hurler)'],
  },
  {
    id: 'mrna',
    name: 'mRNA /\nLNP Delivery',
    color: '#5bc8f5',
    x: 0.82, y: 0.82,
    proof: 'COVID-19 vaccines demonstrated the platform at scale; Oxlumo (PH1, 2020) first approved RNAi/LNP for a rare metabolic disease',
    desc: 'Lipid nanoparticles deliver mRNA or siRNA instructions directly to cells. The COVID-19 vaccines validated safety and scalability. Oxlumo (lumasiran, 2020) is the first approved RNAi/LNP therapy for primary hyperoxaluria. Moderna\'s mRNA-3927 for propionic acidemia is in Phase 1/2 trials, with preclinical programs underway for PKU and Fabry disease.',
    diseases: ['PKU', 'Fabry Disease', 'Pompe Disease'],
  },
];

// ─── ACT 4: APPROVAL TIMELINE ─────────────────────────────────────────────────
// Corrections from prior audit:
//   • Cerezyme: 1993 → 1994 (FDA approval May 23, 1994; accessdata.fda.gov label)
//   • Fabrazyme: 2001 → 2003 (US FDA approval April 2003; 2001 was EMA)
//   • Casgevy Beta-Thal: was listed as 2023 "simultaneous" → corrected to Jan 16, 2024
//   • Alhemo/PH1: wrong drug. Alhemo (concizumab) is for hemophilia A/B with inhibitors.
//     The RNAi for PH1 is Oxlumo (lumasiran), approved Nov 23, 2020.
//   • Elevidys: accelerated approval was June 22, 2023 (not 2024); 2024 was the
//     traditional-approval expansion; indication restricted to ambulatory patients Nov 2025.
//   • Added: Roctavian (Hemophilia A, 2023), Skyclarys (Friedreich's Ataxia, 2023),
//     Oxlumo (PH1, 2020), Alhemo (Hemophilia A/B, 2024) with correct indication.
//   • Spinraza note corrected: not "first ASO for a neuromuscular disease"
//     (eteplirsen for DMD preceded it by 3 months); updated to "first for SMA / first intrathecal CNS ASO".
//   • Givlaari note corrected: second RNAi drug ever approved (Onpattro Aug 2018 was first).

const APPROVALS = [
  { year: 1983, label: 'Orphan Drug Act',
    note: 'US legislation creates financial incentives for rare disease research',
    color: '#929db3', type: 'policy'  },

  { year: 1991, label: 'Ceredase — Gaucher',
    note: 'First enzyme replacement therapy for a lysosomal storage disorder',
    color: '#f5a623', type: 'therapy' },

  { year: 1994, label: 'Cerezyme — Gaucher',
    note: 'Recombinant ERT — more scalable manufacturing, same principle (FDA: May 23, 1994; prior file listed 1993, which was EU)',
    color: '#f5a623', type: 'therapy' },

  { year: 2003, label: 'Fabrazyme — Fabry',
    note: 'First ERT for Fabry disease (US FDA approval April 2003; prior file listed 2001, which was EMA)',
    color: '#f5a623', type: 'therapy' },

  { year: 2006, label: 'Myozyme — Pompe',
    note: 'ERT for Pompe disease, addressing cardiac and respiratory involvement in infants',
    color: '#f5a623', type: 'therapy' },

  { year: 2016, label: 'Spinraza — SMA',
    note: 'First disease-modifying therapy for SMA; first intrathecally delivered CNS ASO (Dec 23, 2016). Note: eteplirsen for DMD was first ASO for a neuromuscular disease, approved 3 months earlier.',
    color: '#b48cfa', type: 'therapy' },

  { year: 2017, label: 'Luxturna — Retinal Dystrophy',
    note: 'First in vivo gene therapy approved in the US',
    color: '#00e5b0', type: 'therapy' },

  { year: 2019, label: 'Zolgensma — SMA',
    note: 'First systemic AAV gene therapy — single-dose treatment priced at $2.1M',
    color: '#00e5b0', type: 'therapy' },

  { year: 2019, label: 'Givlaari — AHP',
    note: 'First RNAi therapy for acute hepatic porphyria. Second RNAi drug ever approved — Onpattro (patisiran, Aug 2018) for hATTR amyloidosis was first.',
    color: '#5bc8f5', type: 'therapy' },

  { year: 2020, label: 'Oxlumo — PH1',
    note: 'First RNAi/LNP therapy for primary hyperoxaluria type 1 (rare kidney stone disease). Prior file incorrectly listed "Alhemo" here — Alhemo is for hemophilia, not PH1.',
    color: '#5bc8f5', type: 'therapy' },

  { year: 2021, label: 'Lumakras — KRAS cancers',
    note: 'Targeting previously undruggable KRAS G12C mutations — expanding rare cancer categories',
    color: '#5bc8f5', type: 'therapy' },

  { year: 2022, label: 'Hemgenix — Hemophilia B',
    note: 'First gene therapy for hemophilia B — one infusion replacing ongoing factor injections',
    color: '#00e5b0', type: 'therapy' },

  { year: 2022, label: 'PDE Natural History Study',
    note: 'International PDE Consortium publishes largest natural history dataset for ALDH7A1-PDE — foundation for AAV gene therapy IND filing',
    color: '#f5a623', type: 'policy'  },

  { year: 2023, label: 'Casgevy — Sickle Cell',
    note: 'First CRISPR-based therapy to receive regulatory approval (Dec 8, 2023)',
    color: '#f5a623', type: 'therapy' },

  { year: 2023, label: 'Tofersen — ALS (SOD1)',
    note: 'ASO therapy (Qalsody) for SOD1 mutation ALS — platform from SMA research; accelerated approval April 2023',
    color: '#b48cfa', type: 'therapy' },

  { year: 2023, label: "Skyclarys — Friedreich's Ataxia",
    note: "First disease-modifying treatment approved for Friedreich's ataxia (omaveloxolone; Nrf2 activator, not gene therapy; Feb 2023)",
    color: '#b48cfa', type: 'therapy' },

  { year: 2023, label: 'Roctavian — Hemophilia A',
    note: 'First AAV gene therapy for hemophilia A (valoctocogene roxaparvovec, BioMarin; approved June 2023)',
    color: '#00e5b0', type: 'therapy' },

  { year: 2023, label: 'Elevidys — Duchenne MD',
    note: 'First gene therapy for DMD; accelerated approval June 22, 2023 (traditional approval + label expansion to ages 4+: June 2024; indication restricted to ambulatory patients Nov 2025 following fatal liver injury reports)',
    color: '#00e5b0', type: 'therapy' },

  { year: 2024, label: 'Casgevy — Beta-Thalassemia',
    note: 'Same CRISPR approach approved for transfusion-dependent beta-thalassemia (Jan 16, 2024) — six weeks after and a separate approval from the sickle cell indication',
    color: '#f5a623', type: 'therapy' },

  { year: 2024, label: 'Alhemo — Hemophilia A/B',
    note: 'RNAi-based therapy (concizumab) for hemophilia A or B with inhibitors (approved Dec 20, 2024). Prior file incorrectly described this as an RNAi for PH1 — that drug is Oxlumo (2020).',
    color: '#ff6b8a', type: 'therapy' },
];

// ─── SOURCES & NOTES ──────────────────────────────────────────────────────────
const SOURCES = [
  {
    claim: '300 million people live with a rare disease; cumulative point prevalence 3.5–5.9%',
    cite: 'Nguengang Wakap S, et al. Estimating cumulative point prevalence of rare diseases: analysis of the Orphanet database. Eur J Hum Genet. 2020.',
    url: 'https://www.nature.com/articles/s41431-019-0508-0',
  },
  {
    claim: 'Over 7,000 known rare diseases; new conditions described regularly',
    cite: 'NIH Genetic and Rare Diseases Information Center (GARD); Orphanet Report Series.',
    url: 'https://rarediseases.info.nih.gov/',
  },
  {
    claim: 'Rare disease defined as affecting fewer than 1 in 2,000 people (EU); fewer than 200,000 persons (US Orphan Drug Act)',
    cite: 'European Commission — Orphan Medicinal Products.',
    url: 'https://health.ec.europa.eu/medicinal-products/orphan-medicinal-products_en',
  },
  {
    claim: 'Roughly 70% of rare diseases begin in childhood',
    cite: 'EURORDIS — What is a rare disease?',
    url: 'https://www.eurordis.org/information-support/what-is-a-rare-disease/',
  },
  {
    claim: '~95% of rare diseases have no FDA-approved treatment (approximate; figure varies by source and year)',
    cite: 'National Organization for Rare Disorders (NORD); EveryLife Foundation for Rare Diseases.',
    url: 'https://rarediseases.org/',
  },
  {
    claim: 'Many rare diseases share common biological mechanisms that can be targeted across conditions',
    cite: 'Brooks PJ. Targeting shared molecular etiologies to accelerate drug development for rare diseases. EMBO Mol Med. 2023.',
    url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC10331571/',
  },
 
  // ── Orphan Drug Act & regulatory ──────────────────────────────────────────
  {
    claim: 'Orphan Drug Act (1983) — creates financial incentives for rare disease drug development',
    // FIXED: previous URL was 404. Corrected path below.
    cite: 'FDA — Orphan Drug Act: Relevant Excerpts (Pub. L. 97-414).',
    url: 'https://www.fda.gov/industry/designating-orphan-product-drugs-and-biological-products/orphan-drug-act-relevant-excerpts',
  },
 
  // ── Therapy approvals ─────────────────────────────────────────────────────
  {
    claim: 'Therapy approval dates and indications (all FDA drug approval records)',
    cite: 'U.S. Food & Drug Administration — Drugs@FDA: FDA-Approved Drugs.',
    url: 'https://www.accessdata.fda.gov/scripts/cder/daf/',
  },
  {
    claim: 'AAV gene therapy approvals: Zolgensma (SMA, 2019), Hemgenix (Hemophilia B, 2022), Roctavian (Hemophilia A, 2023), Elevidys (DMD, 2023)',
    cite: 'FDA — Approved Cellular and Gene Therapy Products.',
    url: 'https://www.fda.gov/vaccines-blood-biologics/cellular-gene-therapy-products/approved-cellular-and-gene-therapy-products',
  },
  {
    claim: 'AAV gene therapy in clinical trials for Friedreich\'s Ataxia (LX2006, Lexeo) and Pompe disease; PDE IND filings',
    cite: 'ClinicalTrials.gov — registry of ongoing clinical studies.',
    url: 'https://clinicaltrials.gov/',
  },
  {
    claim: 'Ceredase (alglucerase, 1991) — first enzyme replacement therapy for a lysosomal storage disorder',
    cite: 'NINDS — Alglucerase injection (Ceredase®) for Gaucher disease.',
    url: 'https://www.ninds.nih.gov/about-ninds/what-we-do/impact/ninds-contributions-approved-therapies/alglucerase-injection-ceredaser-gaucher-disease',
  },
  {
    claim: 'Cerezyme (imiglucerase) FDA approval: May 23, 1994 (not 1993; EMA approval was different)',
    cite: 'FDA accessdata — Cerezyme label; Initial U.S. Approval: 1994.',
    url: 'https://www.accessdata.fda.gov/drugsatfda_docs/label/2021/020367_S119lbl.pdf',
  },
  {
    claim: 'Fabrazyme (agalsidase beta) FDA approval: April 2003; EMA approval was 2001',
    cite: 'Fabry Disease News — Fabrazyme (agalsidase beta) for Fabry disease.',
    url: 'https://fabrydiseasenews.com/fabrazyme-is-first-fabry-disease-therapy/',
  },
  {
    claim: 'Spinraza (nusinersen, Dec 2016) — first disease-modifying therapy for SMA; first intrathecal CNS ASO. Note: eteplirsen for DMD (Sept 2016) was the first ASO approved for any neuromuscular disease.',
    // FIXED: claim text corrected. Previous version said "first ASO for a neuromuscular disease"
    // which is inaccurate — eteplirsen (Exondys 51) for DMD was approved 3 months earlier.
    cite: 'NINDS — Nusinersen (Spinraza®) for SMA.',
    url: 'https://www.ninds.nih.gov/about-ninds/what-we-do/impact/ninds-contributions-approved-therapies/nusinersen-spinrazar-spinal-muscular-atrophy-sma',
  },
  {
    claim: 'Four exon-skipping ASOs approved for Duchenne MD: eteplirsen (2016), golodirsen (2019), viltolarsen (2020), casimersen (2021)',
    cite: 'Aartsma-Rus et al. — Antisense Oligonucleotide-Mediated Exon-skipping Therapies: Precision Medicine Spreading from DMD. PMC8355726.',
    url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC8355726/',
  },
  {
    claim: 'Casgevy (exagamglogene autotemcel) — first CRISPR therapy approved: UK MHRA Nov 2023; US FDA Dec 8, 2023 (SCD); Jan 16, 2024 (beta-thalassemia — separate approval, not simultaneous)',
    cite: 'Parums DV. First Regulatory Approvals for CRISPR-Cas9 Therapeutic Gene Editing. Med Sci Monit. 2024. PMC10913280.',
    url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC10913280/',
  },
  {
    claim: 'Elevidys (delandistrogene moxeparvovec): accelerated approval June 22, 2023; traditional approval + label expansion June 2024; indication restricted to ambulatory patients November 2025 following fatal liver injury reports',
    cite: 'FDA — Approves New Safety Warning and Revised Indication for Elevidys (Nov 2025).',
    url: 'https://www.fda.gov/news-events/press-announcements/fda-approves-new-safety-warning-and-revised-indication-limits-use-elevidys-following-reports-fatal',
  },
  {
    claim: 'Tofersen (Qalsody): accelerated approval April 25, 2023 for SOD1-ALS',
    cite: 'Ionis Pharmaceuticals — FDA approves QALSODY (tofersen).',
    url: 'https://ir.ionis.com/news-releases/news-release-details/fda-approves-qalsodytm-tofersen-first-treatment-targeting',
  },
  {
    claim: 'mRNA-LNP therapies for rare metabolic diseases: Moderna mRNA-3927 (propionic acidemia, Phase 1/2); preclinical programs in PKU and Fabry',
    cite: 'Grunewald S, et al. Interim analyses of a first-in-human phase 1/2 mRNA trial for propionic acidaemia. Nature. 2024.',
    url: 'https://www.nature.com/articles/s41586-024-07266-7',
  },
 
  // ── Global disease prevalence ──────────────────────────────────────────────
  {
    claim: 'CF: ~105,000 diagnosed globally in 94 countries (Guo et al., 2024); total estimated ~188,000 including undiagnosed',
    cite: 'Guo J, et al. International disparities in diagnosis and treatment access for cystic fibrosis. Pediatr Pulmonol. 2024.',
    url: 'https://onlinelibrary.wiley.com/doi/full/10.1002/ppul.26954',
  },
  {
    claim: "Huntington's: pooled global prevalence 4.88 per 100,000 (~390,000 worldwide)",
    cite: 'Medina A, et al. Prevalence and Incidence of HD: Updated Systematic Review and Meta-Analysis. Mov Disord. 2022.',
    url: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC10086981/',
  },
  {
    claim: 'DMD: global prevalence ~4.8 per 100,000 general population (~300,000 worldwide)',
    cite: 'Salari N, et al. Global prevalence of DMD and BMD: systematic review and meta-analysis. J Orthop Surg Res. 2022.',
    url: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC8848641/',
  },
  {
    claim: 'Sickle Cell Disease: 7.74 million people living with SCD globally in 2021 (100,000 is US-only)',
    cite: 'GBD 2021 Sickle Cell Disease Collaborators. Global, regional, and national prevalence and mortality burden. Lancet Haematol. 2023.',
    url: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC10390339/',
  },
  {
    claim: 'Beta-Thalassemia: ~200,000 patients on regular transfusions globally (TIF registry); ~60,000 affected births/year (WHO) — not a prevalence figure',
    cite: 'Higgs DR, Engel JD, Stamatoyannopoulos G. Thalassaemia. Lancet. 2012. (NIH/PMC review)',
    url: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC2893117/',
  },
  {
    claim: 'Hemophilia A: 208,957 diagnosed globally; Hemophilia B: 42,203 (WFH Annual Survey 2022). True global burden ~1.125M (all types, all severities)',
    cite: 'Angchaisuksiri P, et al. Haemophilia care in Asia. Haemophilia. 2024. (citing WFH 2022 Annual Global Survey)',
    url: 'https://onlinelibrary.wiley.com/doi/full/10.1111/hae.14998',
  },
  {
    claim: 'Pompe disease: global birth prevalence 2.0 per 100,000 live births (meta-analysis, 2024)',
    cite: 'Wang X, et al. Global birth prevalence of Pompe disease: systematic review and meta-analysis. Neuroscience. 2024.',
    url: 'https://www.sciencedirect.com/science/article/abs/pii/S0306452224004962',
  },
  {
    claim: 'ALS: ~222,801 cases globally in 2015, projected to increase to ~376,674 by 2040',
    cite: 'Arthur KC, et al. Projected increase in amyotrophic lateral sclerosis from 2015 to 2040. Nat Commun. 2016.',
    url: 'https://www.nature.com/articles/ncomms12408',
  },
  {
    claim: 'Prader-Willi: 350,000–400,000 individuals worldwide',
    cite: 'Rare Disease Advisor — Prader-Willi Syndrome Epidemiology.',
    url: 'https://www.rarediseaseadvisor.com/disease-info-pages/prader-willi-syndrome-epidemiology/',
  },
  {
    claim: "Friedreich's Ataxia: average global prevalence ~0.5 per 100,000; US prevalence ~1 in 40,000–50,000",
    cite: "Reetz K, et al. Friedreich's ataxia — a rare multisystem disease. Lancet Neurol. 2025.",
    url: 'https://www.thelancet.com/journals/laneur/article/PIIS1474-4422(25)00175-9/fulltext',
  },
  {
    claim: 'Gaucher disease: global prevalence 0.9 per 100,000 (~72,000 worldwide)',
    cite: 'Chen Y, et al. Global Epidemiology of Gaucher Disease: Updated Systematic Review and Meta-Analysis. PMC10115488. 2023.',
    url: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC10115488/',
  },
 
  // ── NIH funding crisis ─────────────────────────────────────────────────────
  {
    claim: 'NIH FY2026 budget history and proposed ~40% cut to $27.9B; final FY2026 appropriation was $47.22B (signed Feb 3, 2026)',
    // FIXED: replaced AAAS URL (bot-blocked) with CRS R43341 (publicly available, authoritative).
    cite: 'Congressional Research Service R43341 — National Institutes of Health (NIH) Funding: FY1996–FY2026.',
    url: 'https://www.congress.gov/crs-product/R43341',
  },
  {
    claim: 'FY2026 budget proposed consolidating NIH\'s 27 institutes into 8; Brookings analysis of research impact',
    cite: 'Brookings Institution — The 2026 Health and Health Care Budget (Jun 2025).',
    url: 'https://www.brookings.edu/articles/the-2026-health-and-health-care-budget/',
  },
  {
    claim: '~1,200 NIH staff received RIF notices on April 1, 2025; broader internal targets of 3,400–5,000 positions',
    // FIXED: replaced science.org/topic/nih (bot-blocked) with specific, accessible article.
    cite: 'Science/AAAS — NIH research grant funding rates plummeted in 2025 (Feb 2026).',
    url: 'https://www.science.org/content/article/nih-research-grant-success-rates-plummeted-2025',
  },
  {
    claim: 'At least 2,370 active NIH grants terminated in 2025; AAMC found ~$5B less in grant commitments Jan–Jun 2025 vs. prior year',
    cite: 'AAMC — Clinical Trials and Research Training Stalled as Billions in NIH Funds Sit Idle (Aug 2025).',
    url: 'https://www.aamc.org/about-us/mission-areas/biomedical-research/publication/clinical-trials-and-research-training-stalled-billions-nih-funds-sit-idle',
  },
  {
    claim: '383 NIH-funded clinical trials disrupted (1 in 30); 74,311 patients enrolled in affected active trials',
    cite: 'AJMC — NIH Grant Terminations Disrupt 1 in 30 Clinical Trials (JAMA Intern Med, 2025).',
    url: 'https://www.ajmc.com/view/nih-grant-terminations-disrupt-1-in-30-clinical-trials-impacting-over-74-000-participants',
  },
  {
    claim: 'Rare disease researchers warn NIH funding disruptions directly threaten pipeline therapies',
    cite: 'Rare Disease Advisor — NIH Budget Cuts Could Threaten Progress and Endanger Lives (May 2025).',
    url: 'https://www.rarediseaseadvisor.com/features/nih-budget-cuts-threaten-research-warn-experts-rare-diseases/',
  },
  {
    claim: 'FY2026 budget proposed consolidating NCATS into NIGMS (not elimination); Congress rejected this in final appropriations bill',
    cite: 'NCATS — Budget page (official NIH source).',
    url: 'https://ncats.nih.gov/about/budget',
  },
  {
    claim: '15% indirect cost cap announced Feb 7, 2025; permanently enjoined by D. Mass. (Apr 2025); upheld by First Circuit (Jan 5, 2026)',
    // FIXED: replaced aau.edu homepage with the specific First Circuit ruling coverage.
    cite: 'Harvard Magazine — U.S. Appeals Court Preserves NIH Research Funding (Jan 2026).',
    url: 'https://www.harvardmagazine.com/research/harvard-court-preserves-nih-research-funding',
  },
];

// ─── UTILS ────────────────────────────────────────────────────────────────────
function formatCount(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(0)}M`;
  if (n >= 100_000)   return `${(n / 1_000).toFixed(0)}K`;
  if (n >= 1_000) {
    const k = n / 1_000;
    return k === Math.floor(k) ? `${k.toFixed(0)}K` : `${k.toFixed(1)}K`;
  }
  return n.toLocaleString();
}

function bubbleRadius(patients) {
  return Math.max(28, Math.min(90, Math.sqrt(patients / 180000) * 90));
}

const CATEGORY_REGIONS = {
  Hematologic:  { cx: 0.18, cy: 0.28 },
  Metabolic:    { cx: 0.50, cy: 0.28 },
  Neurological: { cx: 0.82, cy: 0.28 },
  Pulmonary:    { cx: 0.18, cy: 0.62 },
  Muscular:     { cx: 0.50, cy: 0.62 },
  Connective:   { cx: 0.82, cy: 0.62 },
};

function layoutBubbles(diseases, W, H) {
  const byCategory = {};
  diseases.forEach((d) => {
    (byCategory[d.category] ||= []).push(d);
  });

  const result = [];
  const regionRX = W * 0.14;
  const regionRY = H * 0.18;

  Object.entries(byCategory).forEach(([cat, list]) => {
    const region = CATEGORY_REGIONS[cat] || { cx: 0.5, cy: 0.5 };
    const cx = W * region.cx;
    const cy = H * region.cy;
    const n  = list.length;
    list.forEach((d, i) => {
      const r = bubbleRadius(d.patients);
      let x, y;
      if (n === 1) {
        x = cx; y = cy;
      } else {
        const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
        const dist  = i === 0 ? 0 : 1;
        x = cx + Math.cos(angle) * regionRX * dist * 0.7;
        y = cy + Math.sin(angle) * regionRY * dist * 0.7;
      }
      result.push({ ...d, x, y, r });
    });
  });
  return result;
}

const BUBBLE_REVEAL_ORDER = ['Hematologic', 'Metabolic', 'Neurological', 'Pulmonary', 'Muscular', 'Connective'];

const DOT_COLORS_BY_CAT = [
  'rgba(255,107,138,',
  'rgba(245,166,35,',
  'rgba(180,140,250,',
  'rgba(0,229,176,',
  'rgba(91,200,245,',
  'rgba(91,200,245,',
];

const STABLE_DOT_TARGETS = BUBBLE_REVEAL_ORDER.map((cat) => {
  const region = CATEGORY_REGIONS[cat] || { cx: 0.5, cy: 0.5 };
  return { x: region.cx, y: region.cy };
});

function generateDotArrangements(numDots) {
  let seed = 7531;
  const rand = () => { seed = (seed * 1103515245 + 12345) & 0x7fffffff; return seed / 0x7fffffff; };

  const numClusters = 38;
  const clusters = [{ cx: 0.5, cy: 0.5 }];
  for (let i = 1; i < numClusters; i++) {
    clusters.push({ cx: 0.08 + rand() * 0.84, cy: 0.13 + rand() * 0.74 });
  }

  const result = [];
  for (let i = 0; i < numDots; i++) {
    const offSide = i % 4;
    const offX = (() => {
      if (offSide === 0) return -0.10;
      if (offSide === 1) return 1.10;
      return 0.05 + rand() * 0.90;
    })();
    const offY = (() => {
      if (offSide === 2) return -0.10;
      if (offSide === 3) return 1.10;
      return 0.05 + rand() * 0.90;
    })();
    const off = { x: offX, y: offY, hidden: true };

    const phases = [];

    phases.push(i === 0 ? { x: 0.5, y: 0.5 } : off);

    if (i === 0) {
      phases.push({ x: 0.5, y: 0.5 });
    } else if (i < 25) {
      const a = ((i - 1) / 24) * Math.PI * 2;
      const r = 0.06 + rand() * 0.02;
      phases.push({ x: 0.5 + Math.cos(a) * r, y: 0.5 + Math.sin(a) * r });
    } else {
      phases.push(off);
    }

    if (i < 280) {
      const ci = i < 25 ? 0 : 1 + ((i - 25) % (numClusters - 1));
      const c = clusters[ci];
      const a = rand() * Math.PI * 2;
      const r = 0.005 + rand() * 0.025;
      phases.push({ x: c.cx + Math.cos(a) * r, y: c.cy + Math.sin(a) * r });
    } else {
      phases.push(off);
    }

    if (i < 1100) {
      phases.push({ x: 0.04 + rand() * 0.92, y: 0.42 + (rand() - 0.5) * 0.32 });
    } else {
      phases.push(off);
    }

    if (i < 1600) {
      phases.push({ x: 0.05 + rand() * 0.90, y: 0.15 + rand() * 0.70 });
    } else {
      phases.push(off);
    }

    if (i < 2300) {
      phases.push({ x: 0.03 + rand() * 0.94, y: 0.06 + rand() * 0.88 });
    } else {
      phases.push(off);
    }

    const cat = i % 6;
    const region = STABLE_DOT_TARGETS[cat] || { x: 0.5, y: 0.5 };
    const cloudAngle = rand() * Math.PI * 2;
    const cloudR = 0.03 + Math.sqrt(rand()) * 0.24;
    phases.push({
      x: Math.max(0.02, Math.min(0.98, region.x + Math.cos(cloudAngle) * cloudR)),
      y: Math.max(0.04, Math.min(0.96, region.y + Math.sin(cloudAngle) * cloudR * 1.05)),
    });

    result.push(phases);
  }
  return result;
}

// ─── DOT CANVAS ───────────────────────────────────────────────────────────────
function DotCanvas({ arrangementPhase = 0, arrangementBlend = 0, catPulls = [0,0,0,0,0,0], targets }) {
  const canvasRef   = useRef(null);
  const dotsRef     = useRef([]);
  const rafRef      = useRef(null);
  const phaseRef    = useRef(arrangementPhase);
  const blendRef    = useRef(arrangementBlend);
  const pullsRef    = useRef(catPulls);
  const targetsRef  = useRef(targets);
  const DOT_COUNT   = 3200;

  useEffect(() => { phaseRef.current = arrangementPhase; }, [arrangementPhase]);
  useEffect(() => { blendRef.current = arrangementBlend; }, [arrangementBlend]);
  useEffect(() => { pullsRef.current = catPulls; }, [catPulls]);
  useEffect(() => {
    targetsRef.current = targets;
    if (targets && dotsRef.current.length) {
      dotsRef.current.forEach((d) => {
        const t = targets[d.cat] || { x: 0.5, y: 0.5 };
        d.tx = t.x + d.tjx;
        d.ty = t.y + d.tjy;
      });
    }
  }, [targets]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      canvas.width  = canvas.offsetWidth  * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
    };
    resize();
    window.addEventListener('resize', resize);

    const arrangements = generateDotArrangements(DOT_COUNT);
    const initialTargets = targetsRef.current || Array(6).fill({ x: 0.5, y: 0.5 });

    dotsRef.current = arrangements.map((arr, i) => {
      const cat = i % 6;
      const tjx = (((i * 31) % 100) / 100 - 0.5) * 0.10;
      const tjy = (((i * 47) % 100) / 100 - 0.5) * 0.12;
      const target = initialTargets[cat] || { x: 0.5, y: 0.5 };
      return {
        x: arr[0].x, y: arr[0].y,
        arr,
        r: i === 0 ? 2.6 : (((i * 13) % 100) / 100) * 1.5 + 0.9,
        cat, tjx, tjy,
        tx: target.x + tjx,
        ty: target.y + tjy,
        lerp: 0.04 + (((i * 7) % 100) / 100) * 0.07,
      };
    });

    const ctx = canvas.getContext('2d');
    const draw = () => {
      const W = canvas.width, H = canvas.height;
      const dpr = window.devicePixelRatio;
      ctx.clearRect(0, 0, W, H);

      const phase = phaseRef.current;
      const blend = blendRef.current;
      const nextPhase = Math.min(6, phase + 1);
      const pulls = pullsRef.current;

      dotsRef.current.forEach((d) => {
        const pull = pulls[d.cat] ?? 0;
        let targetX, targetY, drawR, alpha;

        if (pull > 0) {
          if (pull >= 1) return;
          const eased = pull < 0.5
            ? 4 * pull * pull * pull
            : 1 - Math.pow(-2 * pull + 2, 3) / 2;
          const from = d.arr[6];
          targetX = from.x * (1 - eased) + d.tx * eased;
          targetY = from.y * (1 - eased) + d.ty * eased;
          drawR = d.r * (1 - Math.pow(pull, 2.5) * 0.55);
          alpha = pull < 0.6 ? 0.92 : 0.92 * (1 - (pull - 0.6) / 0.4);
        } else {
          const cur = d.arr[phase];
          const nxt = d.arr[nextPhase];
          if (cur.hidden && nxt.hidden) return;
          targetX = cur.x * (1 - blend) + nxt.x * blend;
          targetY = cur.y * (1 - blend) + nxt.y * blend;
          drawR = d.r;
          if (cur.hidden && !nxt.hidden) alpha = 0.88 * blend;
          else if (!cur.hidden && nxt.hidden) alpha = 0.88 * (1 - blend);
          else alpha = 0.88;
        }

        d.x += (targetX - d.x) * d.lerp;
        d.y += (targetY - d.y) * d.lerp;

        if (d.x < -0.04 || d.x > 1.04 || d.y < -0.04 || d.y > 1.04) return;
        if (alpha < 0.02 || drawR < 0.15) return;

        ctx.beginPath();
        ctx.arc(d.x * W, d.y * H, drawR * dpr, 0, Math.PI * 2);
        ctx.fillStyle = DOT_COLORS_BY_CAT[d.cat] + alpha.toFixed(3) + ')';
        ctx.fill();
      });

      rafRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => { window.removeEventListener('resize', resize); cancelAnimationFrame(rafRef.current); };
  }, []);

  return <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />;
}

// ─── BUBBLE TRANSFORM ─────────────────────────────────────────────────────────
function BubbleTransform({ transformProgress, highlightCategory }) {
  const svgRef    = useRef(null);
  const layoutRef = useRef(null);
  const [dims, setDims] = useState({ w: 1200, h: 800 });

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const parent = el.parentElement;
    if (!parent) return;
    const updateDims = () => {
      const w = parent.clientWidth;
      const h = parent.clientHeight;
      if (w > 0 && h > 0) setDims({ w, h });
    };
    updateDims();
    const ro = new ResizeObserver(updateDims);
    ro.observe(parent);
    return () => ro.disconnect();
  }, []);

  if (!layoutRef.current || layoutRef.current._w !== dims.w) {
    layoutRef.current = layoutBubbles(DISEASES, dims.w, dims.h);
    layoutRef.current._w = dims.w;
  }
  const laid = layoutRef.current;
  const rScale = dims.w < 500 ? 0.38 : dims.w < 768 ? 0.55 : 1;

  const uniqueColors = [...new Set(DISEASES.map(d => d.color))];

  return (
    <svg ref={svgRef} viewBox={`0 0 ${dims.w} ${dims.h}`}
         preserveAspectRatio="xMidYMid meet"
         style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
      <defs>
        {uniqueColors.flatMap((c) => {
          const id = c.replace('#', '');
          return [
            <linearGradient key={`b-${id}`} id={`bubble-${id}`} x1="50%" y1="0%" x2="50%" y2="100%">
              <stop offset="0%"   stopColor={c} stopOpacity="0.26" />
              <stop offset="100%" stopColor={c} stopOpacity="0.16" />
            </linearGradient>,
          ];
        })}
      </defs>

      {laid.map((d, i) => {
        const isHL = !highlightCategory || d.category === highlightCategory;
        const N = BUBBLE_REVEAL_ORDER.length;
        const categoryIdx = BUBBLE_REVEAL_ORDER.indexOf(d.category);
        const slotWidth = 1 / N;
        const categoryStart = categoryIdx * slotWidth;
        const categoryProgress = Math.max(0, Math.min(1, (transformProgress - categoryStart) / slotWidth));
        const bubbleEase = 1 - Math.pow(1 - categoryProgress, 2.2);
        const bubbleOpacity = bubbleEase;
        const bubbleScale = 0.55 + bubbleEase * 0.45;
        const id = d.color.replace('#', '');
        const textOpacity = Math.max(0, Math.min(1, (categoryProgress - 0.5) / 0.4));
        const r = d.r * rScale;
        const labelFontSize = Math.max(7, Math.min(14, r * 0.26));
        const countFontSize = Math.max(6, Math.min(11, r * 0.19));
        const isLarge = rScale < 0.7 ? r > 20 : d.r > 38;
        return (
          <g key={d.name}
             opacity={bubbleOpacity * (isHL ? 1 : 0.22)}
             transform={`translate(${d.x},${d.y}) scale(${bubbleScale}) translate(${-d.x},${-d.y})`}
             style={{ transition: 'opacity 0.3s' }}>
            <circle cx={d.x} cy={d.y} r={r * 1.18}
              fill={d.color} opacity={isHL ? 0.04 : 0.015} />
            <circle cx={d.x} cy={d.y} r={r}
              fill={`url(#bubble-${id})`}
              stroke={d.color}
              strokeWidth={isHL ? 1.6 : 0.7}
              strokeOpacity={isHL ? 0.78 : 0.32}
            />

            {isLarge ? (
              <>
                <text x={d.x} y={d.y - 2} textAnchor="middle" fontSize={labelFontSize}
                  fill="#eef2f8" fontFamily="'Syne', sans-serif" fontWeight="500"
                  opacity={textOpacity}
                  style={{ pointerEvents: 'none', letterSpacing: '0.01em' }}>
                  {rScale < 0.7 ? (d.short || (d.name.length > 12 ? d.name.split(' ')[0] : d.name)) : d.name}
                </text>
                <text x={d.x} y={d.y + labelFontSize + 3} textAnchor="middle" fontSize={countFontSize}
                  fill={d.color} fontFamily="'IBM Plex Mono', monospace"
                  opacity={textOpacity * 0.85}
                  style={{ pointerEvents: 'none', letterSpacing: '0.08em' }}>
                  {formatCount(d.patients)}
                </text>
              </>
            ) : (
              <>
                <text x={d.x} y={d.y - 2} textAnchor="middle" fontSize={Math.max(6, 9 * rScale / 0.7)}
                  fill="#eef2f8" fontFamily="'Syne', sans-serif" fontWeight="500"
                  opacity={textOpacity}
                  style={{ pointerEvents: 'none' }}>
                  {d.short || (d.name.length > 12 ? d.name.split(' ')[0] : d.name)}
                </text>
                <text x={d.x} y={d.y + Math.max(5, 8 * rScale / 0.7)} textAnchor="middle" fontSize={Math.max(5, 8 * rScale / 0.7)}
                  fill={d.color} fontFamily="'IBM Plex Mono', monospace"
                  opacity={textOpacity * 0.85}
                  style={{ pointerEvents: 'none', letterSpacing: '0.06em' }}>
                  {formatCount(d.patients)}
                </text>
              </>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// ─── COUNTER ──────────────────────────────────────────────────────────────────
function Counter({ value }) {
  const displayed = Math.floor(value * 300_000_000);
  return (
    <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 'clamp(1.2rem, 3vw, 2rem)', color: '#00e5b0', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
      {displayed.toLocaleString()}
      <span style={{ fontSize: '0.55em', color: '#6b7590', marginLeft: '0.5rem' }}>/ 300,000,000</span>
    </div>
  );
}

// ─── ACT 3: TREATMENT MATRIX ──────────────────────────────────────────────────
function TreatmentMatrix({ activePlatform, revealedEdges, isMobile }) {
  const sortedDiseases = DISEASES
    .map(d => ({
      ...d,
      coverage: PLATFORMS.filter(p => p.diseases.includes(d.name)).length,
    }))
    .sort((a, b) => {
      if (b.coverage !== a.coverage) return b.coverage - a.coverage;
      return b.patients - a.patients;
    });

  const platformsByDisease = {};
  PLATFORMS.forEach(p => {
    p.diseases.forEach(d => {
      (platformsByDisease[d] ||= new Set()).add(p.id);
    });
  });

  const SHORT_LABEL = { aav: 'AAV', aso: 'ASO', crispr: 'CRISPR', ert: 'ERT', mrna: 'mRNA' };

  return (
    <div style={{
      width: '100%', height: '100%', minWidth: isMobile ? '520px' : 'auto',
      display: 'flex', flexDirection: 'column',
      fontFamily: "'IBM Plex Mono', monospace",
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: `minmax(0, 180px) repeat(${PLATFORMS.length}, minmax(0, 1fr))`,
        alignItems: 'end',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        paddingBottom: '0.7rem',
        marginBottom: '0.4rem',
        gap: '0.4rem',
      }}>
        <div style={{ ...S.mono, fontSize: '0.5rem', letterSpacing: '0.2em', color: '#6b7590', textTransform: 'uppercase' }}>
          Disease · patients
        </div>
        {PLATFORMS.map(p => {
          const isActive = p.id === activePlatform;
          const isRevealed = revealedEdges.includes(p.id);
          return (
            <div key={p.id} style={{
              textAlign: 'center',
              opacity: isRevealed ? 1 : 0.35,
              transition: 'opacity 0.5s',
            }}>
              <div style={{
                ...S.mono, fontWeight: 500,
                fontSize: isActive ? '0.9rem' : '0.78rem',
                color: isRevealed ? p.color : '#3a4255',
                lineHeight: 1, marginBottom: '0.3rem',
                letterSpacing: '0.05em',
                transition: 'all 0.4s',
                whiteSpace: 'nowrap',
              }}>
                {SHORT_LABEL[p.id] || p.id.toUpperCase()}
              </div>
              <div style={{ ...S.mono, fontSize: '0.5rem', letterSpacing: '0.18em', color: isActive ? p.color : '#3a4255', textTransform: 'uppercase' }}>
                · {p.diseases.length} ·
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: '1px' }}>
        {sortedDiseases.map((d, rowIdx) => {
          const rowPlatforms = platformsByDisease[d.name] || new Set();
          const rowHasActive = activePlatform && rowPlatforms.has(activePlatform);
          const rowEverTreated = [...rowPlatforms].some(pid => revealedEdges.includes(pid));
          return (
            <div key={d.name} style={{
              display: 'grid',
              gridTemplateColumns: `minmax(0, 180px) repeat(${PLATFORMS.length}, minmax(0, 1fr))`,
              alignItems: 'center',
              padding: '0.25rem 0',
              gap: '0.4rem',
              background: rowHasActive ? `${PLATFORMS.find(p => p.id === activePlatform)?.color}10` : 'transparent',
              transition: 'background 0.4s',
              minHeight: 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingRight: '0.5rem' }}>
                <div style={{
                  width: '3px', alignSelf: 'stretch',
                  background: d.color,
                  opacity: rowEverTreated ? 0.9 : 0.35,
                  borderRadius: '1px',
                  transition: 'opacity 0.4s',
                }} />
                <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
                  <div style={{
                    ...S.syne, fontWeight: 500,
                    fontSize: '0.72rem',
                    color: rowHasActive ? '#f6f9ff' : rowEverTreated ? '#dde3f0' : '#929db3',
                    letterSpacing: '0.01em',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    transition: 'color 0.4s',
                    flex: 1, minWidth: 0,
                  }}>
                    {d.name}
                  </div>
                  <div style={{ ...S.mono, fontSize: '0.55rem', color: '#6b7590', letterSpacing: '0.06em', flexShrink: 0 }}>
                    {formatCount(d.patients)}
                  </div>
                </div>
              </div>

              {PLATFORMS.map(p => {
                const treated = p.diseases.includes(d.name);
                const isRevealed = revealedEdges.includes(p.id);
                const isActive = p.id === activePlatform;
                const showFilled = treated && isRevealed;
                return (
                  <div key={p.id} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    {showFilled ? (
                      <div style={{
                        width: isActive ? '18px' : '14px',
                        height: isActive ? '18px' : '14px',
                        borderRadius: '50%',
                        background: p.color,
                        opacity: isActive ? 1 : 0.7,
                        boxShadow: isActive ? `0 0 10px ${p.color}88` : `0 0 4px ${p.color}55`,
                        transition: 'all 0.35s ease',
                      }} />
                    ) : treated ? (
                      <div style={{
                        width: '6px', height: '6px', borderRadius: '50%',
                        border: `1px solid ${p.color}33`,
                      }} />
                    ) : (
                      <div style={{
                        width: '3px', height: '3px', borderRadius: '50%',
                        background: '#1d2230',
                      }} />
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── ACT 4: TIMELINE ──────────────────────────────────────────────────────────
const TYPE_LABELS = { policy: 'Policy', therapy: 'Approval' };

function Timeline({ revealCount, isMobile }) {
  const shown = APPROVALS.slice(0, revealCount);

  return (
    <div style={{ position: 'relative', paddingBottom: '4rem' }}>
      <div style={{
        position: 'absolute', left: isMobile ? '8px' : '50%', top: 0, bottom: 0,
        width: '1px', background: 'rgba(255,255,255,0.06)',
        transform: isMobile ? 'none' : 'translateX(-50%)',
      }} />

      {shown.map((a, i) => {
        const side     = isMobile ? 'right' : (i % 2 === 0 ? 'right' : 'left');
        const prevYear = i > 0 ? APPROVALS[i - 1].year : 1978;
        const yearGap  = Math.max(0, a.year - prevYear);
        const marginTop = i === 0 ? '1rem' : `${Math.max(14, yearGap * (isMobile ? 8 : 22))}px`;

        return (
          <div key={`${a.year}-${a.label}`} style={{
            position: 'relative',
            display: 'flex',
            justifyContent: isMobile ? 'flex-start' : (side === 'left' ? 'flex-start' : 'flex-end'),
            paddingLeft: isMobile ? '24px' : 0,
            marginTop,
            opacity: 0,
            animation: 'fadeInUp 0.5s ease both',
          }}>
            <div style={{
              position: 'absolute', left: isMobile ? '8px' : '50%', top: '1rem',
              width: '8px', height: '8px', borderRadius: '50%',
              background: a.color, boxShadow: `0 0 8px ${a.color}`,
              transform: 'translateX(-50%)', zIndex: 1,
            }} />

            <div style={{
              width: isMobile ? '100%' : '44%',
              padding: '0.7rem 1rem',
              background: `${a.color}10`,
              border: `1px solid ${a.color}30`,
              borderRadius: '2px',
              textAlign: isMobile ? 'left' : (side === 'right' ? 'left' : 'right'),
            }}>
              <div style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: '0.6rem', letterSpacing: '0.1em',
                color: a.color, marginBottom: '0.2rem', textTransform: 'uppercase',
              }}>
                {a.year} · {TYPE_LABELS[a.type]}
              </div>
              <div style={{ fontFamily: "'Syne', sans-serif", fontSize: '0.85rem', color: '#dde3f0', lineHeight: 1.3, marginBottom: '0.3rem' }}>
                {a.label}
              </div>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.68rem', color: '#929db3', lineHeight: 1.6 }}>
                {a.note}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── SHARED STYLES ────────────────────────────────────────────────────────────
const S = {
  mono: { fontFamily: "'IBM Plex Mono', monospace" },
  serif: { fontFamily: "'Instrument Serif', serif" },
  syne: { fontFamily: "'Syne', sans-serif" },
  label: { fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.6rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#00e5b0', marginBottom: '1.2rem' },
  muted: { color: '#929db3' },
  divider: { borderTop: '1px solid rgba(255,255,255,0.05)' },
};

function Bridge({ eyebrow, heading, sub, headingColor = '#dde3f0' }) {
  return (
    <div style={{ textAlign: 'center', padding: '8rem 2rem', background: '#07080a', ...S.divider }}>
      <div style={{ ...S.label, marginBottom: '1.5rem' }}>{eyebrow}</div>
      <p style={{ ...S.serif, fontSize: 'clamp(2rem, 4vw, 3.2rem)', lineHeight: 1.15, maxWidth: '680px', margin: '0 auto 1.5rem', color: headingColor }}
        dangerouslySetInnerHTML={{ __html: heading }} />
      <p style={{ ...S.mono, fontSize: 'clamp(0.75rem, 1.2vw, 0.88rem)', ...S.muted, maxWidth: '520px', margin: '0 auto', lineHeight: 1.85 }}>
        {sub}
      </p>
    </div>
  );
}

// ─── SOURCES (COLLAPSIBLE) ────────────────────────────────────────────────
function SourcesSection({ isMobile }) {
  const [expanded, setExpanded] = useState(false);
  const listRef = useRef(null);
  const MAX_H = isMobile ? 220 : 320;

  return (
    <div style={{ background: '#0b0f1a', borderTop: '1px solid rgba(255,255,255,0.06)', padding: isMobile ? '3rem 1.2rem 4rem' : '5rem 2rem 6rem' }}>
      <div style={{ maxWidth: '760px', margin: '0 auto' }}>
        <button
          onClick={() => setExpanded(p => !p)}
          style={{
            all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', width: '100%', marginBottom: expanded ? '1rem' : 0,
          }}
        >
          <div style={{ ...S.label, margin: 0 }}>Sources &amp; Notes</div>
          <span style={{
            ...S.mono, fontSize: '0.65rem', color: '#6b7590', letterSpacing: '0.1em',
            display: 'flex', alignItems: 'center', gap: '0.4rem',
          }}>
            {expanded ? 'COLLAPSE' : `${SOURCES.length} REFERENCES`}
            <span style={{
              display: 'inline-block', transition: 'transform 0.3s ease',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              fontSize: '0.8rem',
            }}>&#9662;</span>
          </span>
        </button>

        <div style={{
          maxHeight: expanded ? '2000px' : `${MAX_H}px`,
          overflow: 'hidden',
          transition: 'max-height 0.5s ease',
          position: 'relative',
        }}>
          <p style={{ ...S.mono, fontSize: isMobile ? '0.65rem' : '0.72rem', ...S.muted, lineHeight: 1.8, marginBottom: '1.2rem', marginTop: expanded ? 0 : '1rem' }}>
            Figures on this page are rounded for clarity and reflect widely-cited
            estimates. Rare-disease prevalence and patient counts vary by source
            and methodology.
          </p>
          <ol ref={listRef} style={{
            ...S.mono, fontSize: isMobile ? '0.62rem' : '0.72rem', ...S.muted,
            lineHeight: 1.9, paddingLeft: isMobile ? '1rem' : '1.4rem', margin: 0,
            display: 'flex', flexDirection: 'column', gap: '0.7rem',
          }}>
            {SOURCES.map((s, i) => (
              <li key={i} style={{ paddingLeft: '0.3rem' }}>
                <span style={{ color: '#7a8294' }}>{s.claim}</span>
                {' — '}
                {s.url ? (
                  <a href={s.url} target="_blank" rel="noopener noreferrer"
                     style={{ color: '#00e5b0', textDecoration: 'none', borderBottom: '1px solid rgba(0,229,176,0.3)' }}>
                    {s.cite}
                  </a>
                ) : (
                  <span style={{ color: '#9aa3b6' }}>{s.cite}</span>
                )}
              </li>
            ))}
          </ol>

          {/* Fade overlay when collapsed */}
          {!expanded && (
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0, height: '80px',
              background: 'linear-gradient(to bottom, rgba(11,15,26,0) 0%, #0b0f1a 100%)',
              pointerEvents: 'none',
            }} />
          )}
        </div>

        {!expanded && (
          <button
            onClick={() => setExpanded(true)}
            style={{
              all: 'unset', cursor: 'pointer', display: 'block', width: '100%',
              textAlign: 'center', marginTop: '0.8rem',
              ...S.mono, fontSize: '0.65rem', letterSpacing: '0.15em', color: '#00e5b0',
              padding: '0.8rem 0', textTransform: 'uppercase',
              borderBottom: '1px solid rgba(0,229,176,0.2)',
              transition: 'color 0.2s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#33f0c8'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#00e5b0'}
          >
            Show all {SOURCES.length} sources
          </button>
        )}
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function RareScaleEducational() {
  const wrapRef       = useRef(null);
  const act1Ref       = useRef(null);
  const act3Ref       = useRef(null);
  const act4Ref       = useRef(null);
  const act4PanelRef  = useRef(null);
  const copyRef       = useRef(null);
  const isFirst       = useRef(true);

  const [arrangementPhase,  setArrangementPhase]  = useState(0);
  const [arrangementBlend,  setArrangementBlend]  = useState(0);
  const [counterProgress,   setCounterProgress]   = useState(0);
  const [copyIndex,         setCopyIndex]          = useState(0);
  const [act1Opacity,       setAct1Opacity]        = useState(1);
  const [bubbleProgress,    setBubbleProgress]     = useState(0);
  const [highlightCategory, setHighlightCategory]  = useState(null);

  const [act3Opacity,       setAct3Opacity]        = useState(0);
  const [activePlatform,    setActivePlatform]     = useState(null);
  const [revealedEdges,     setRevealedEdges]      = useState([]);

  const [act4Opacity,       setAct4Opacity]        = useState(0);
  const [timelineReveal,    setTimelineReveal]     = useState(0);
  const [isMobile,          setIsMobile]           = useState(false);

  useEffect(() => {
    if (!document.querySelector(`link[href="${FONT_URL}"]`)) {
      const l = Object.assign(document.createElement('link'), { rel: 'stylesheet', href: FONT_URL });
      document.head.appendChild(l);
    }
    const root = document.getElementById('root');
    const prev = {
      bodyOverflow: document.body.style.overflow,
      bodyHeight: document.body.style.minHeight,
      rootOverflow: root?.style.overflow,
      rootHeight: root?.style.height,
    };
    document.body.style.overflow = 'visible';
    document.body.style.minHeight = 'auto';
    if (root) { root.style.overflow = 'visible'; root.style.height = 'auto'; }
    return () => {
      document.body.style.overflow = prev.bodyOverflow;
      document.body.style.minHeight = prev.bodyHeight;
      if (root) { root.style.overflow = prev.rootOverflow; root.style.height = prev.rootHeight; }
    };
  }, []);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    const el = act4PanelRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [timelineReveal]);

  useEffect(() => {
    if (isFirst.current) { isFirst.current = false; return; }
    if (!copyRef.current) return;
    gsap.fromTo(copyRef.current, { opacity: 0, y: 12 }, { opacity: 1, y: 0, duration: 0.45, ease: 'power2.out' });
  }, [copyIndex]);

  useEffect(() => {
    const ctx = gsap.context(() => {

      const PHASE1_END = 0.35;
      const PHASE2_END = 0.82;
      ScrollTrigger.create({
        trigger: act1Ref.current,
        start: 'top top', end: 'bottom top', scrub: 0.6,
        onUpdate: (self) => {
          const p = self.progress;

          if (p <= PHASE1_END) {
            const phase1 = p / PHASE1_END;
            setCounterProgress(phase1);
            let idx = 0, blend = 0;
            for (let i = ACT1_LINES.length - 1; i >= 0; i--) {
              if (phase1 >= ACT1_LINES[i].at) {
                idx = i;
                if (i < ACT1_LINES.length - 1) {
                  const span = ACT1_LINES[i + 1].at - ACT1_LINES[i].at;
                  blend = span > 0 ? (phase1 - ACT1_LINES[i].at) / span : 0;
                }
                break;
              }
            }
            setCopyIndex(idx);
            setArrangementPhase(idx);
            setArrangementBlend(Math.max(0, Math.min(1, blend)));
            setBubbleProgress(0);
          } else {
            const phase2 = Math.min(1, (p - PHASE1_END) / (PHASE2_END - PHASE1_END));
            setCounterProgress(1);
            setCopyIndex(ACT1_LINES.length - 1);
            setArrangementPhase(6);
            setArrangementBlend(0);
            setBubbleProgress(phase2);
          }
        },
      });

      ScrollTrigger.create({
        trigger: act3Ref.current,
        start: 'top 80%', end: 'top 20%', scrub: 0.8,
        onUpdate: (self) => {
          setAct1Opacity(1 - self.progress);
          setAct3Opacity(self.progress);
        },
      });

      PLATFORMS.forEach((platform, i) => {
        ScrollTrigger.create({
          trigger: act3Ref.current,
          start: `${8 + i * 18}% center`,
          onEnter: () => {
            setRevealedEdges(prev => prev.includes(platform.id) ? prev : [...prev, platform.id]);
            setActivePlatform(platform.id);
          },
          onLeaveBack: () => {
            const revealed = PLATFORMS.slice(0, i).map(p => p.id);
            setRevealedEdges(revealed);
            setActivePlatform(i > 0 ? PLATFORMS[i - 1].id : null);
          },
        });
      });

      ScrollTrigger.create({
        trigger: act4Ref.current,
        start: 'top 75%', end: 'top 25%', scrub: 0.8,
        onUpdate: (self) => {
          setAct3Opacity(1 - self.progress);
          setAct4Opacity(self.progress);
        },
      });

      ScrollTrigger.create({
        trigger: act4Ref.current,
        start: '5% top', end: 'bottom bottom', scrub: 0.5,
        onUpdate: (self) => {
          setTimelineReveal(Math.floor(self.progress * APPROVALS.length));
        },
      });

    }, wrapRef);

    return () => ctx.revert();
  }, []);

  const currentLine  = ACT1_LINES[copyIndex];
  const isBigMoment  = copyIndex === ACT1_LINES.length - 1;
  const activePlatformData = PLATFORMS.find(p => p.id === activePlatform);

  return (
    <div ref={wrapRef} style={{ background: '#07080a', color: '#dde3f0', fontFamily: "'Syne', sans-serif", overflowX: 'clip' }}>

      {(() => null)()}
      <section ref={act1Ref} style={{ height: '1250vh', position: 'relative' }}>
        {(() => {
          const N = BUBBLE_REVEAL_ORDER.length;
          const slot = 1 / N;
          const catPulls = BUBBLE_REVEAL_ORDER.map((_, idx) => {
            return Math.max(0, Math.min(1, (bubbleProgress - idx * slot) / slot));
          });

          const dotTargets = STABLE_DOT_TARGETS;

          const activeIdx = Math.min(N - 1, Math.floor(bubbleProgress * N));
          const activeCategory = bubbleProgress > 0 ? BUBBLE_REVEAL_ORDER[activeIdx] : null;
          const activeColor = activeCategory ? CATEGORY_COLORS[activeCategory] : '#00e5b0';

          const revealedCount = bubbleProgress <= 0 ? 0 : Math.min(N, Math.ceil(bubbleProgress * N));

          return (
            <div style={{
              position: 'sticky', top: 0, height: '100vh',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden', opacity: act1Opacity, transition: 'opacity 0.1s',
            }}>
              <div style={{ position: 'absolute', inset: 0 }}>
                <DotCanvas arrangementPhase={arrangementPhase} arrangementBlend={arrangementBlend} catPulls={catPulls} targets={dotTargets} />
              </div>
              <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 60% 70% at 50% 50%, rgba(7,8,10,0.3) 0%, rgba(7,8,10,0.72) 100%)', pointerEvents: 'none', opacity: Math.max(0, 1 - bubbleProgress * 2) }} />

              <div style={{ opacity: Math.max(0, 1 - bubbleProgress * 12), display: 'contents' }}>
                <div style={{ position: 'relative', zIndex: 2, textAlign: 'center', marginBottom: '1.5rem', opacity: Math.max(0, 1 - bubbleProgress * 12) }}>
                  <Counter value={counterProgress} />
                </div>
                <div style={{ position: 'relative', zIndex: 2, maxWidth: '560px', textAlign: 'center', padding: '0 2rem', opacity: Math.max(0, 1 - bubbleProgress * 12) }}>
                  <div ref={copyRef}>
                    <p style={{
                      ...( isBigMoment ? S.serif : S.mono ),
                      fontSize: isBigMoment ? 'clamp(1.4rem, 3vw, 2.2rem)' : 'clamp(0.8rem, 1.5vw, 1rem)',
                      lineHeight: isBigMoment ? 1.25 : 1.85,
                      color: isBigMoment ? '#dde3f0' : '#b0b8c8',
                      whiteSpace: 'pre-line',
                      textShadow: '0 1px 8px rgba(7,8,10,0.9), 0 0 20px rgba(7,8,10,0.7)',
                    }}>
                      {currentLine.text}
                    </p>
                  </div>
                </div>
              </div>
              {copyIndex === 0 && bubbleProgress === 0 && (
                <div style={{ position: 'absolute', bottom: '2.5rem', ...S.mono, fontSize: '0.6rem', letterSpacing: '0.2em', color: '#6b7590', textTransform: 'uppercase', animation: 'rsBounce 2s ease infinite' }}>
                  ↓ scroll
                </div>
              )}

              {bubbleProgress > 0 && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5 }}>
                  <div style={{ width: '90%', height: '85%', maxWidth: '1200px' }}>
                    <BubbleTransform transformProgress={bubbleProgress} highlightCategory={highlightCategory} />
                  </div>
                </div>
              )}

              {bubbleProgress > 0 && (() => {
                const allRevealed = bubbleProgress >= 0.999;
                return (
                  <>
                    <div style={{
                      position: 'absolute', top: '6vh', left: 0, right: 0,
                      textAlign: 'center', zIndex: 10, pointerEvents: 'none',
                      opacity: Math.min(1, bubbleProgress * 4),
                    }}>
                      {allRevealed ? (
                        <>
                          <div style={{ ...S.label, marginBottom: '0.6rem', color: '#00e5b0' }}>
                            7,000 conditions
                          </div>
                          <div style={{ ...S.serif, fontSize: 'clamp(1.6rem, 3vw, 2.4rem)', color: '#dde3f0', letterSpacing: '0.01em' }}>
                            Each bubble represents a patient population — <em style={{ color: '#00e5b0', fontStyle: 'italic' }}>most still lack approved treatments.</em>
                          </div>
                        </>
                      ) : (
                        <>
                          {/* <div style={{ ...S.label, marginBottom: '0.6rem', color: activeColor }}>
                            Category {activeIdx + 1} of {N}
                          </div> */}
                          <div style={{ ...S.serif, fontSize: 'clamp(1.6rem, 3vw, 2.4rem)', color: activeColor, letterSpacing: '0.01em' }}>
                            {activeCategory}
                          </div>
                        </>
                      )}
                    </div>

                    {/* <div style={{
                      position: 'absolute', bottom: '5vh', left: 0, right: 0,
                      textAlign: 'center', zIndex: 10, pointerEvents: 'none',
                      opacity: Math.min(1, bubbleProgress * 4),
                    }}>
                      <p style={{ ...S.mono, fontSize: 'clamp(0.75rem, 1.2vw, 0.88rem)', ...S.muted, maxWidth: '560px', margin: '0 auto', lineHeight: 1.85 }}>
                        Showing 24 of 7,000+ rare diseases. Bubble size reflects estimated global patient population. The 300 million figure above represents all rare diseases worldwide.
                      </p>
                      <div style={{
                        ...S.mono, fontSize: '0.65rem', letterSpacing: '0.2em',
                        color: allRevealed ? '#00e5b0' : '#6b7590',
                        marginTop: '1rem', textTransform: 'uppercase',
                        transition: 'color 0.4s',
                      }}>
                        {revealedCount} of {N} categories revealed
                      </div>
                    </div> */}
                  </>
                );
              })()}
            </div>
          );
        })()}
      </section>

      {/* Bridge 2→3 */}
      <Bridge
        eyebrow="A key insight"
        heading="Many of these diseases<br/><em style='color:#00e5b0;font-style:italic'>share common biological mechanisms.</em>"
        sub="The diseases below are a representative sample — 24 of over 7,000 — with estimated global patient counts. A therapy developed for one condition can often inform treatments for others."
      />

      {/* ══ ACT 3: PLATFORM COVERAGE ═════════════════════════════════════════ */}
      <section ref={act3Ref} style={{ height: '400vh', position: 'relative' }}>
        <div style={{
          position: 'sticky', top: 0, height: '100vh',
          display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gridTemplateRows: isMobile ? 'auto 1fr' : 'auto', alignItems: 'stretch',
          overflow: 'hidden', opacity: act3Opacity, transition: 'opacity 0.15s',
        }}>
          <div style={{ position: 'relative', height: '100%', minHeight: 0, overflow: isMobile ? 'auto' : 'hidden', padding: isMobile ? '2rem 0.5rem' : '4.5rem 1.5rem 5rem 3rem', order: isMobile ? 2 : 0, WebkitOverflowScrolling: 'touch' }}>
            <TreatmentMatrix activePlatform={activePlatform} revealedEdges={revealedEdges} isMobile={isMobile} />
            {activePlatformData && (() => {
              const treatedSet = new Set();
              revealedEdges.forEach(pid => {
                const p = PLATFORMS.find(pp => pp.id === pid);
                if (p) p.diseases.forEach(d => treatedSet.add(d));
              });
              return (
                <div style={{
                  position: 'absolute', bottom: '3vh', left: isMobile ? '1rem' : '3rem',
                  zIndex: 5, pointerEvents: 'none',
                  display: isMobile ? 'none' : 'flex', alignItems: 'baseline', gap: '1.2rem',
                  animation: 'rsFadeUp 0.4s ease',
                }}>
                  <div>
                    <div style={{ ...S.mono, fontSize: '0.55rem', letterSpacing: '0.2em', color: '#6b7590', textTransform: 'uppercase', marginBottom: '0.2rem' }}>
                      Platforms revealed
                    </div>
                    <div style={{ ...S.mono, fontSize: '1.2rem', color: activePlatformData.color, letterSpacing: '0.04em' }}>
                      {revealedEdges.length}<span style={{ fontSize: '0.65em', color: '#6b7590' }}> / {PLATFORMS.length}</span>
                    </div>
                  </div>
                  <div style={{ width: '1px', height: '34px', background: 'rgba(255,255,255,0.08)' }} />
                  <div>
                    <div style={{ ...S.mono, fontSize: '0.55rem', letterSpacing: '0.2em', color: '#6b7590', textTransform: 'uppercase', marginBottom: '0.2rem' }}>
                      Diseases reached
                    </div>
                    <div style={{ ...S.mono, fontSize: '1.2rem', color: '#dde3f0', letterSpacing: '0.04em' }}>
                      {treatedSet.size}<span style={{ fontSize: '0.65em', color: '#6b7590' }}> / {DISEASES.length}</span>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          <div style={{ padding: isMobile ? '1.5rem 1rem' : '3rem 5vw 3rem 2rem', borderLeft: isMobile ? 'none' : '1px solid rgba(255,255,255,0.05)', borderBottom: isMobile ? '1px solid rgba(255,255,255,0.05)' : 'none', height: isMobile ? 'auto' : '100%', minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', justifyContent: isMobile ? 'flex-start' : 'center', order: isMobile ? 1 : 0 }}>
            <div style={S.label}>The Overlap</div>
            {!activePlatformData ? (
              <div>
                <h2 style={{ ...S.serif, fontSize: isMobile ? 'clamp(1.2rem, 4vw, 1.6rem)' : 'clamp(1.8rem, 3vw, 2.8rem)', lineHeight: 1.1, marginBottom: isMobile ? '0.6rem' : '1.2rem' }}>
                  Understanding what these diseases{isMobile ? ' ' : <br />}have in common.
                </h2>
                {!isMobile && (
                  <p style={{ ...S.mono, fontSize: '0.8rem', lineHeight: 1.85, ...S.muted }}>
                    Rather than viewing each disease in isolation, researchers have identified shared biological mechanisms. A small number of therapeutic strategies can potentially address many conditions.
                  </p>
                )}
              </div>
            ) : (
              <div style={{ animation: 'rsFadeUp 0.4s ease' }}>
                <div style={{ display: 'inline-block', ...S.mono, fontSize: '0.6rem', letterSpacing: '0.1em', textTransform: 'uppercase', padding: '0.3rem 0.8rem', border: `1px solid ${activePlatformData.color}40`, background: `${activePlatformData.color}12`, color: activePlatformData.color, marginBottom: isMobile ? '0.5rem' : '1rem', borderRadius: '2px' }}>
                  Therapy Platform
                </div>
                <h2 style={{ ...S.serif, fontSize: isMobile ? 'clamp(1.1rem, 4vw, 1.4rem)' : 'clamp(1.6rem, 2.5vw, 2.4rem)', lineHeight: 1.1, marginBottom: isMobile ? '0.4rem' : '0.8rem', color: activePlatformData.color }}>
                  {activePlatformData.name.replace('\n', ' ')}
                </h2>
                <div style={{ ...S.mono, fontSize: isMobile ? '0.6rem' : '0.68rem', color: activePlatformData.color, letterSpacing: '0.06em', marginBottom: isMobile ? '0.5rem' : '1rem', lineHeight: 1.5, opacity: 0.8 }}>
                  ↳ {activePlatformData.proof}
                </div>
                {!isMobile && (
                  <p style={{ ...S.mono, fontSize: '0.78rem', lineHeight: 1.85, ...S.muted, marginBottom: '1.5rem' }}>
                    {activePlatformData.desc}
                  </p>
                )}
                {!isMobile && (
                  <>
                    <div style={{ ...S.mono, fontSize: '0.65rem', letterSpacing: '0.1em', color: '#6b7590', textTransform: 'uppercase', marginBottom: '0.6rem' }}>
                      Connected diseases
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                      {activePlatformData.diseases.map(d => (
                        <span key={d} style={{ ...S.mono, fontSize: '0.62rem', padding: '0.2rem 0.6rem', border: `1px solid ${activePlatformData.color}30`, color: activePlatformData.color, background: `${activePlatformData.color}08`, borderRadius: '2px' }}>
                          {d}
                        </span>
                  ))}
                </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Bridge 3→4 */}
      <Bridge
        eyebrow="The trajectory"
        heading="Each approval builds on<br/><em style='color:#00e5b0;font-style:italic'>the work that came before it.</em>"
        sub="For most of medical history, rare diseases had no treatments. Over the past four decades, the pace of therapeutic development has accelerated significantly."
      />

      {/* ══ ACT 4: TIMELINE ═══════════════════════════════════════════════ */}
      <section ref={act4Ref} style={{ minHeight: '400vh', position: 'relative' }}>
        <div style={{
          position: 'sticky', top: 0, height: '100vh',
          overflow: 'hidden', opacity: act4Opacity, transition: 'opacity 0.15s',
          display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr', gridTemplateRows: isMobile ? 'auto 1fr' : 'auto', alignItems: 'start',
        }}>
          <div style={{ padding: isMobile ? '2rem 1.5rem 1rem' : '6rem 3rem 4rem 6vw', position: isMobile ? 'relative' : 'sticky', top: 0 }}>
            <div style={S.label}>Milestones</div>
            <h2 style={{ ...S.serif, fontSize: 'clamp(2rem, 3vw, 2.8rem)', lineHeight: 1.1, marginBottom: '1.5rem' }}>
              Four decades of progress, with increasing pace.
            </h2>
            <p style={{ ...S.mono, fontSize: '0.78rem', lineHeight: 1.85, ...S.muted, marginBottom: '2rem' }}>
              In 1983, the Orphan Drug Act created financial incentives for rare disease drug development. The rate of approvals has grown steadily since then, with notable acceleration in recent years.
            </p>
            <div style={{ display: isMobile ? 'none' : 'flex', flexDirection: 'column', gap: '0.8rem' }}>
              {[['#00e5b0','AAV Gene Therapy'],['#b48cfa','ASO Platform'],['#f5a623','CRISPR'],['#ff6b8a','Enzyme Replacement'],['#5bc8f5','mRNA / RNAi'],['#5a637a','Policy']].map(([col, lbl]) => (
                <div key={lbl} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: col, flexShrink: 0 }} />
                  <span style={{ ...S.mono, fontSize: '0.68rem', color: '#929db3', letterSpacing: '0.06em' }}>{lbl}</span>
                </div>
              ))}
            </div>
            <div style={{ marginTop: isMobile ? '1rem' : '3rem', padding: '1.2rem', background: 'rgba(0,229,176,0.04)', border: '1px solid rgba(0,229,176,0.15)', display: isMobile ? 'none' : 'block' }}>
              <div style={{ ...S.mono, fontSize: '0.65rem', color: '#00e5b0', marginBottom: '0.4rem', letterSpacing: '0.1em' }}>2020 – 2024</div>
              <div style={{ ...S.mono, fontSize: '0.78rem', ...S.muted, lineHeight: 1.6 }}>
                More gene and cell therapies were approved in this period than in the entire previous decade.
              </div>
            </div>
          </div>

          <div ref={act4PanelRef} style={{ padding: isMobile ? '1rem 1rem' : '4rem 6vw 4rem 2rem', overflowY: 'auto', height: isMobile ? '100%' : '100vh', minHeight: 0 }}>
            <Timeline revealCount={timelineReveal} isMobile={isMobile} />
          </div>
        </div>
      </section>

      {/* ══ CLOSING ═══════════════════════════════════════════════════════ */}
      <div style={{ textAlign: 'center', padding: '10rem 2rem 8rem', background: 'linear-gradient(to bottom, #07080a 0%, #0b0f1a 100%)', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <p style={{ ...S.mono, fontSize: '0.6rem', letterSpacing: '0.2em', color: '#00e5b0', textTransform: 'uppercase', marginBottom: '2rem' }}>
          Looking ahead
        </p>
        <p style={{ ...S.serif, fontSize: 'clamp(2.5rem, 6vw, 5.5rem)', lineHeight: 1.0, maxWidth: '800px', margin: '0 auto 2rem', letterSpacing: '-0.02em' }}>
          300 million people<br />
          <em style={{ color: '#00e5b0', fontStyle: 'italic' }}>and growing research momentum.</em>
        </p>
        <p style={{ ...S.mono, fontSize: 'clamp(0.8rem, 1.4vw, 1rem)', ...S.muted, maxWidth: '480px', margin: '0 auto 3rem', lineHeight: 1.85 }}>
          The scientific platforms are maturing. Each rare disease with an approved treatment provides a foundation for addressing the next.
        </p>
        {/* <p style={{ ...S.serif, fontSize: 'clamp(1rem, 2vw, 1.5rem)', color: '#6b7590', fontStyle: 'italic' }}>
          The pace of progress continues to accelerate.
        </p> */}
      </div>

      {/* ══ NIH FUNDING CRISIS ═════════════════════════════════════════ */}
      <div style={{ background: '#0a0c12', borderTop: '2px solid rgba(255,107,138,0.35)', padding: '7rem 2rem' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto' }}>
          <div style={{ ...S.label, marginBottom: '1.2rem', color: '#ff6b8a' }}>A critical moment</div>
          <p style={{ ...S.serif, fontSize: 'clamp(2rem, 5vw, 3.6rem)', lineHeight: 1.08, letterSpacing: '-0.02em', marginBottom: '1.6rem' }}>
            This progress is<br />
            <em style={{ color: '#ff6b8a', fontStyle: 'italic' }}>now at risk.</em>
          </p>
          <p style={{ ...S.mono, fontSize: '0.8rem', color: '#b0b8c8', lineHeight: 1.9, marginBottom: '2.5rem', maxWidth: '600px' }}>
            In 2025, the National Institutes of Health — the single largest public funder of biomedical
      research in the world — faced unprecedented funding disruptions that directly threatened rare
      disease research. The administration's proposed cuts were ultimately rejected by Congress in
      February 2026, but the disruption to ongoing research, clinical trials, and workforce caused
      real and lasting harm.
          </p>

          {/* Key facts */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem', marginBottom: '3rem' }}>
            {[
              {
          stat: '~40%',
          label: 'Proposed NIH budget reduction',
          // CORRECTED: $47.4B → ~$48B (FY2025 appropriation); $27.9B (FY2026 request per CRS).
          // Congress rejected the cuts; final FY2026 appropriation was $47.22B (signed Feb 3, 2026).
          detail: 'The FY2026 executive budget proposed cutting NIH from approximately $48 billion to $27.9 billion — the largest proposed reduction in the agency\'s history. Congress rejected the cuts, passing a final FY2026 budget of $47.2 billion in February 2026. The disruption to research in the interim was nevertheless significant.',
        },
        {
          stat: '~1,200',
          label: 'NIH staff positions eliminated in April 2025 RIF',
          // CORRECTED: "1,500+" → ~1,200 (confirmed per HHS fact sheet and news reports).
          // Broader internal plans targeted 3,400–5,000 total positions.
          detail: 'On April 1, 2025, HHS sent RIF notices to approximately 1,200 NIH employees, cutting communications, HR, procurement, and program officer roles across its 27 institutes. Internal plans circulated earlier in 2025 identified targets of 3,400–5,000 total positions — up to one quarter of NIH\'s workforce.',
        },
        {
          stat: '2,370+',
          label: 'Active NIH grants terminated',
          // CORRECTED: "Thousands frozen" is accurate but imprecise. Senate Appropriations
          // committee letter (2025) cites at least 2,370 active grant terminations.
          // AAMC found ~$5B less in commitments Jan–Jun 2025. 383 clinical trials disrupted
          // (JAMA Internal Medicine). OMB froze $15B unspent FY2025 funds (later reversed).
          detail: 'At least 2,370 active NIH grants were terminated in 2025, according to a Senate Appropriations Committee letter to NIH Director Bhattacharya. An AAMC analysis found nearly $5 billion less in grant commitments between January and June 2025 compared with the same period in 2024. A JAMA Internal Medicine study found 383 active clinical trials disrupted — 1 in 30 — affecting over 74,000 enrolled patients.',
        },
        {
          stat: 'NCATS',
          label: 'Translational sciences center proposed for consolidation',
          // CORRECTED: "proposed for elimination" → "proposed for consolidation into NIGMS".
          // NCATS was not proposed to be eliminated — the FY2026 budget proposed merging it
          // into NIGMS. Congress rejected this in the FY2026 appropriations bill.
          detail: 'The FY2026 budget proposed consolidating the National Center for Advancing Translational Sciences — which runs the rare disease research acceleration programs — into the National Institute of General Medical Sciences. Congress rejected this consolidation in the final FY2026 appropriations bill, and NCATS remains operational.',
        },
        {
          stat: 'ICR cap',
          label: 'Indirect cost rate cap attempted at 15%',
          // CORRECTED: The cap was announced, then blocked. Adding legal status.
          // Permanently enjoined by D. Mass. April 2025; upheld by First Circuit Jan 5, 2026.
          // The FY2026 budget request still proposes the cap, per CRS.
          detail: 'On February 7, 2025, NIH announced a 15% cap on indirect cost reimbursement, threatening the operational budgets of research universities. A federal court blocked the cap with a nationwide injunction (March 5, 2025), which was upheld by the First Circuit Court of Appeals (January 5, 2026). The FY2026 budget request still proposes the cap in legislation, but courts have so far prevented implementation.',
        },
            ].map((item) => (
              <div key={item.label} style={{
                display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '90px 1fr',
                gap: isMobile ? '0.6rem' : '1.2rem', alignItems: 'start',
                padding: isMobile ? '1rem' : '1.2rem 1.4rem',
                background: 'rgba(255,107,138,0.04)',
                border: '1px solid rgba(255,107,138,0.15)',
                borderRadius: '4px',
              }}>
                <div>
                  <div style={{ ...S.mono, fontSize: '1.4rem', fontWeight: 600, color: '#ff6b8a', lineHeight: 1 }}>
                    {item.stat}
                  </div>
                  <div style={{ ...S.mono, fontSize: '0.55rem', color: '#ff6b8a', letterSpacing: '0.1em', textTransform: 'uppercase', marginTop: '0.3rem', lineHeight: 1.3 }}>
                    {item.label}
                  </div>
                </div>
                <div style={{ ...S.mono, fontSize: '0.75rem', color: '#929db3', lineHeight: 1.75 }}>
                  {item.detail}
                </div>
              </div>
            ))}
          </div>

          {/* Why it matters for rare disease */}
          <div style={{ padding: '1.5rem', background: 'rgba(255,107,138,0.06)', border: '1px solid rgba(255,107,138,0.2)', borderRadius: '4px', marginBottom: '3rem' }}>
            <div style={{ ...S.syne, fontSize: '1rem', fontWeight: 600, color: '#dde3f0', marginBottom: '0.8rem' }}>
              Why this matters for rare diseases specifically
            </div>
            <div style={{ ...S.mono, fontSize: '0.78rem', color: '#929db3', lineHeight: 1.85 }}>
              Rare disease research is almost entirely dependent on public funding. Most conditions affect
        too few patients to attract large-scale private investment without initial government-funded
        proof of concept. NIH grants fund the early-stage basic science and natural history studies
        that make every therapy on this page possible. When that pipeline is disrupted, the diseases
        closest to a breakthrough — and the families waiting on them — are the ones who lose the most.
            </div>
          </div>

          {/* What you can do */}
          <div style={{ ...S.label, marginBottom: '1.2rem', color: '#ff6b8a' }}>Take action</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.2rem', marginBottom: '2.5rem' }}>
            {[
              { t: 'Contact your representatives', d: 'Call or write your U.S. senators and House representative. Ask them to protect NIH funding and oppose cuts to rare disease research programs.' },
              { t: 'Submit public comments', d: 'When proposed rules or budget changes open for public comment, submit your perspective. Federal agencies are required to review and respond to public input.' },
              { t: 'Support advocacy organizations', d: 'Groups like the EveryLife Foundation and NORD lobby directly for rare disease research funding at the federal level.' },
            ].map((w) => (
              <div key={w.t} style={{ borderTop: '2px solid #ff6b8a', paddingTop: '1rem' }}>
                <div style={{ ...S.syne, fontSize: '0.9rem', fontWeight: 600, color: '#dde3f0', marginBottom: '0.5rem' }}>{w.t}</div>
                <div style={{ ...S.mono, fontSize: '0.72rem', color: '#929db3', lineHeight: 1.8 }}>{w.d}</div>
              </div>
            ))}
          </div>

          {/* Direct links */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {[
              {
          name: 'Find your representatives',
          desc: 'Look up your senators and House member by zip code.',
          url: 'https://www.congress.gov/members/find-your-member',
        },
        {
          name: 'Research!America — Advocacy toolkit',
          desc: 'Pre-written letters, phone scripts, and guides for contacting elected officials about NIH funding.',
          url: 'https://www.researchamerica.org/take-action',
        },
        {
          name: 'EveryLife Foundation — Policy Center',
          desc: 'Track rare disease legislation and join coordinated advocacy campaigns.',
          url: 'https://everylifefoundation.org/rare-advocates/rare-across-america/',
        },
        {
          name: 'NIH funding history — Congressional Research Service',
          desc: 'Official NIH funding data from FY1996–FY2026, published by the nonpartisan CRS.',
          // CORRECTED: Was "AAAS NIH funding trends" but CRS R43341 is more authoritative
          // and directly citable. CRS report is publicly available at congress.gov.
          url: 'https://www.congress.gov/crs-product/R43341',
        },
            ].map((f) => (
              <a key={f.name} href={f.url} target="_blank" rel="noopener noreferrer"
                 style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1.5rem', padding: '1rem 1.2rem', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '8px', textDecoration: 'none', transition: 'border-color 0.18s ease, background 0.18s ease' }}
                 onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(255,107,138,0.5)'; e.currentTarget.style.background = 'rgba(255,107,138,0.05)'; }}
                 onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}>
                <div>
                  <div style={{ ...S.syne, fontSize: '0.85rem', fontWeight: 600, color: '#dde3f0', marginBottom: '0.25rem' }}>{f.name}</div>
                  <div style={{ ...S.mono, fontSize: '0.68rem', color: '#929db3', lineHeight: 1.6 }}>{f.desc}</div>
                </div>
                <span style={{ ...S.mono, fontSize: '1rem', color: '#ff6b8a', flexShrink: 0 }}>&rarr;</span>
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* ══ HOW YOU CAN HELP ══════════════════════════════════════════════ */}
      <div style={{ background: 'linear-gradient(to bottom, #0b0f1a 0%, #0d1422 100%)', borderTop: '1px solid rgba(0,229,176,0.18)', padding: '7rem 2rem' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto' }}>
          <div style={{ ...S.label, marginBottom: '1.2rem' }}>How You Can Help</div>
          <p style={{ ...S.serif, fontSize: 'clamp(2rem, 5vw, 3.6rem)', lineHeight: 1.08, letterSpacing: '-0.02em', marginBottom: '1.6rem' }}>
            Rare diseases affect<br />
            <em style={{ color: '#00e5b0', fontStyle: 'italic' }}>more people than you might think.</em>
          </p>
          <p style={{ ...S.mono, fontSize: '0.8rem', ...S.muted, lineHeight: 1.9, marginBottom: '3.5rem', maxWidth: '560px' }}>
            Many advances in rare disease research have been supported by patients and families who helped drive awareness and funding. There are several ways to contribute.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.6rem', marginBottom: '4rem' }}>
            {[
              { t: 'Donate', d: 'Support research organizations and patient assistance programs. Sustained funding enables clinical trials.' },
              { t: 'Join a registry', d: 'Patient data helps make clinical trials possible. Contributing to registries supports future therapy development.' },
              { t: 'Spread awareness', d: 'Share information about rare diseases. Greater awareness helps drive the funding and policy support that follows.' },
            ].map((w) => (
              <div key={w.t} style={{ borderTop: '2px solid #00e5b0', paddingTop: '1rem' }}>
                <div style={{ ...S.syne, fontSize: '1rem', fontWeight: 600, color: '#dde3f0', marginBottom: '0.5rem' }}>{w.t}</div>
                <div style={{ ...S.mono, fontSize: '0.72rem', color: '#929db3', lineHeight: 1.8 }}>{w.d}</div>
              </div>
            ))}
          </div>

          <div style={{ ...S.label, marginBottom: '1.2rem', color: '#929db3' }}>Foundations Worth Supporting</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {[
              { name: 'National Organization for Rare Disorders (NORD)', desc: 'Patient assistance programs, advocacy, and research grants.', url: 'https://rarediseases.org' },
              { name: 'Global Genes', desc: 'Connects, equips, and empowers the rare disease community.', url: 'https://globalgenes.org' },
              { name: 'EveryLife Foundation for Rare Diseases', desc: 'Drives science-based public policy for rare disease patients.', url: 'https://everylifefoundation.org' },
              { name: 'Rare Diseases International', desc: 'The global voice advocating for rare disease patients worldwide.', url: 'https://www.rarediseasesinternational.org' },
            ].map((f) => (
              <a key={f.name} href={f.url} target="_blank" rel="noopener noreferrer"
                 style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1.5rem', padding: '1.3rem 1.5rem', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px', textDecoration: 'none', transition: 'border-color 0.18s ease, background 0.18s ease' }}
                 onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(0,229,176,0.5)'; e.currentTarget.style.background = 'rgba(0,229,176,0.05)'; }}
                 onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}>
                <div>
                  <div style={{ ...S.syne, fontSize: '0.9rem', fontWeight: 600, color: '#dde3f0', marginBottom: '0.3rem' }}>{f.name}</div>
                  <div style={{ ...S.mono, fontSize: '0.7rem', color: '#929db3', lineHeight: 1.65 }}>{f.desc}</div>
                </div>
                <span style={{ ...S.mono, fontSize: '1rem', color: '#00e5b0', flexShrink: 0 }}>&rarr;</span>
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* ══ SOURCES & NOTES ═══════════════════════════════════════════════ */}
      <SourcesSection isMobile={isMobile} />

      {/* ══ CONTACT ══════════════════════════════════════════════════════ */}
      <ContactFooter />

      <style>{`
        @keyframes rsBounce { 0%,100%{transform:translateY(0)} 50%{transform:translateY(6px)} }
        @keyframes rsFadeUp  { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
        @keyframes fadeInUp  { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
      `}</style>
    </div>
  );
}
